import { IDataSourceItem } from 'Modules/Studio/DataSources/redux/initialState';
import {
  INGESTION_CONSTANTS,
  INGESTION_STATUS_ICONS,
} from '../../../../utilities/Ingestion.constants';
import { IIngestionDataSourceListState } from '../../../IngestionByDataSource/redux/IngestionDataSourceList.slice';
import { TIngestionStatusLabelProps } from '../IngestionStatusLabel';
import { IActiveIngestion } from 'Modules/Studio/components/StudioInfo/redux/ingestion.interface';

type TProps = Pick<
  TIngestionStatusLabelProps,
  'activeIngestionTables' | 'latestIngestionLog' | 'mode' | 'status' | 'activeIngestion'
> &
  Pick<IIngestionDataSourceListState, 'view'>;

/**
 * Helpers to get status label when ingestion is happening at table level vs datasource level
 * @param activeIngestion
 * @returns string
 */
const getStatusFromTableIngestion = (activeIngestion: IActiveIngestion | null) => {
  const activeIngestionTables = activeIngestion?.tables || {};
  const activeTables = Object.keys(activeIngestionTables);
  const activeTableCount = activeTables.length;

  // ingestion is happening at the datasource level, return status label at datasource level
  if (activeTableCount === 0) {
    return INGESTION_STATUS_ICONS?.[activeIngestion?.status || -1] || '';
  }

  // check if ALL stopping, return 'Stopping on tables'
  const allTablesAreStopping = activeTables.every((tableName) => {
    const status = activeIngestionTables?.[tableName].status;
    return status === INGESTION_CONSTANTS.TRIGGERED_STOP;
  });

  if (allTablesAreStopping) {
    return INGESTION_STATUS_ICONS[INGESTION_CONSTANTS.TRIGGERED_STOP];
  }

  // check if running or stopping, return 'Running on tables'
  const runningOrStoppingOnTables = activeTables.some((tableName) => {
    const status = activeIngestionTables?.[tableName].status;
    return status === INGESTION_CONSTANTS.TRIGGERED_STOP || status === INGESTION_CONSTANTS.RUNNING;
  });

  if (runningOrStoppingOnTables) {
    return INGESTION_STATUS_ICONS[INGESTION_CONSTANTS.RUNNING];
  }

  return '';
};

/**
 * gets the appropriate ingestion status icon string to use, depending on use case
 * @param param0
 * @returns ingestion status icon string
 */
export const getStatusIconString = ({
  activeIngestionTables,
  latestIngestionLog,
  mode,
  status,
  view,
  activeIngestion,
}: TProps): string => {
  if (activeIngestionTables !== undefined && view === 'tables' && mode !== 'TableViewDataSource') {
    // return the status at the individual table level, if active
    if ((latestIngestionLog as IDataSourceItem)?.name in activeIngestionTables) {
      return (
        INGESTION_STATUS_ICONS?.[
          activeIngestionTables?.[(latestIngestionLog as IDataSourceItem)?.name].status
        ] || ''
      );
    }

    return '';
  } else {
    /* use case for:
     * - full ingestion
     * - datasource ingestion
     * - table view - data source ingestion
     */
    if (status) {
      return INGESTION_STATUS_ICONS?.[status] || '';
    }

    // if there are no active ingestions for this datasources AND there is a latest ingestion log with status, report the status
    if (!activeIngestion && latestIngestionLog?.status) {
      return INGESTION_STATUS_ICONS?.[latestIngestionLog?.status] || '';
    }

    // report summary from the table level ingestions
    if (activeIngestion) {
      return getStatusFromTableIngestion(activeIngestion);
    }
  }

  return '';
};
